/* global $ */
import React, { useState, useEffect } from "react";
import axiosInstance from "../axiosInstance";
import Swal from "sweetalert2";
import { useNavigate, useLocation } from "react-router-dom";
import Footer from "../components/Footer";

function AddUser() {

    const navigate = useNavigate();
    const location = useLocation();

    const isEdit = location.state?.isEdit || false;
    const existingUser = location.state?.user || null;

    const [isSaving, setIsSaving] = useState(false);
    const [isResetting, setIsResetting] = useState(false);
    const [loadingData, setLoadingData] = useState(isEdit);

    const [userData, setUserData] = useState({
        user_name: "",
        email_id: "",
        phone_no: "",
        user_type_id: "",
        roll_id: "",
        deparment_id: "",
        profile_picture_url: "",
        isactive: true,
        force_password_change: false
    });

    const [userTypes, setUserTypes] = useState([]);
    const [userRoles, setUserRoles] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [errors, setErrors] = useState({});

    // ─── Password Generator ───────────────────────────────────────────────────────

    const generatePassword = () => {
        const upper = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
        const lower = "abcdefghijklmnopqrstuvwxyz";
        const digits = "0123456789";
        const special = "!@#$%^&*()_+-=";
        const all = upper + lower + digits + special;
        let pwd = "";
        pwd += upper[Math.floor(Math.random() * upper.length)];
        pwd += lower[Math.floor(Math.random() * lower.length)];
        pwd += digits[Math.floor(Math.random() * digits.length)];
        pwd += special[Math.floor(Math.random() * special.length)];
        for (let i = 4; i < 12; i++) {
            pwd += all[Math.floor(Math.random() * all.length)];
        }
        return pwd.split('').sort(() => Math.random() - 0.5).join('');
    };

    // ─── Load Dropdowns ───────────────────────────────────────────────────────────

    useEffect(() => {
        loadUserTypes();
        loadUserRoles();
        loadDepartments();
    }, []);

    const loadUserTypes = async () => {
        try {
            const res = await axiosInstance.get("/api/usertype");
            setUserTypes(res.data.data || res.data || []);
        } catch (err) { console.log(err); }
    };

    const loadUserRoles = async () => {
        try {
            const res = await axiosInstance.get("/api/userroll");
            setUserRoles(res.data.data || res.data || []);
        } catch (err) { console.log(err); }
    };

    const loadDepartments = async () => {
        try {
            const res = await axiosInstance.get("/api/department");
            setDepartments(res.data.data || res.data || []);
        } catch (err) { console.log(err); }
    };

    // ─── Prefill for Edit ─────────────────────────────────────────────────────────

    useEffect(() => {
        if (isEdit && existingUser) {
            prefillUserData();
        }
    }, [isEdit]);

    const prefillUserData = () => {
        setLoadingData(true);
        setUserData({
            user_name: existingUser.user_name || "",
            email_id: existingUser.email_id || "",
            phone_no: existingUser.phone_no || "",
            user_type_id: existingUser.user_type_id || "",
            roll_id: existingUser.roll_id || "",
            deparment_id: existingUser.deparment_id || "",
            profile_picture_url: existingUser.profile_picture_url || "",
            isactive: existingUser.isactive ?? true,
            force_password_change: existingUser.force_password_change ?? false
        });
        setLoadingData(false);
    };

    // ─── Field Handler ────────────────────────────────────────────────────────────

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setUserData({ ...userData, [name]: type === 'checkbox' ? checked : value });
        if (errors[name]) setErrors({ ...errors, [name]: undefined });
    };

    // ─── Validation ───────────────────────────────────────────────────────────────

    const validateForm = () => {
        const formErrors = {};
        if (!userData.user_name.trim()) formErrors.user_name = "Username is required";
        if (!userData.email_id.trim()) formErrors.email_id = "Email is required";
        if (!userData.phone_no.trim()) formErrors.phone_no = "Phone is required";
        if (!userData.user_type_id) formErrors.user_type_id = "User type is required";
        if (!userData.roll_id) formErrors.roll_id = "Role is required";
        if (!userData.deparment_id) formErrors.deparment_id = "Department is required";
        setErrors(formErrors);
        return Object.keys(formErrors).length === 0;
    };

    // ─── Reset Password (Edit mode) ───────────────────────────────────────────────

    const handleResetPassword = async () => {
        const result = await Swal.fire({
            title: 'Reset Password?',
            text: "A new random password will be generated and the user will be required to change it on next login.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, reset it!'
        });

        if (!result.isConfirmed) return;

        setIsResetting(true);
        try {
            const newPassword = generatePassword();
            await axiosInstance.put(`/api/users/${existingUser.id}/password`, {
                password: newPassword
            });
            await axiosInstance.put(`/api/users/${existingUser.id}`, {
                force_password_change: true
            });

            await Swal.fire({
                title: 'Password Reset!',
                html: `
                    <div style="text-align:left;background:#f8f9fa;padding:16px;border-radius:8px;margin-top:8px;">
                        <p style="margin-bottom:8px"><strong>Username:</strong> ${userData.user_name}</p>
                        <p style="margin-bottom:8px"><strong>New Password:</strong> <span style="font-family:monospace;font-size:15px;background:#e9ecef;padding:2px 6px;border-radius:4px">${newPassword}</span></p>
                        <p style="margin-bottom:0;color:#6c757d;font-size:12px">User will be forced to change this password on next login.</p>
                    </div>
                `,
                icon: 'success',
                confirmButtonText: '📋 Copy Credentials',
                showCancelButton: true,
                cancelButtonText: 'Close',
                confirmButtonColor: '#3085d6',
            }).then((r) => {
                if (r.isConfirmed) {
                    navigator.clipboard.writeText(`Username: ${userData.user_name}\nPassword: ${newPassword}`);
                    Swal.fire({ icon: 'success', title: 'Copied!', timer: 1500, showConfirmButton: false });
                }
            });
        } catch (error) {
            Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to reset password' });
        } finally {
            setIsResetting(false);
        }
    };

    // ─── Save ─────────────────────────────────────────────────────────────────────

    const saveUser = async (e) => {
        e.preventDefault();
        if (isSaving) return;
        if (!validateForm()) return;

        setIsSaving(true);

        try {
            const current_user_id = localStorage.getItem("user_id");
            const toNull = (val) => (val === "" || val === undefined ? null : val);

            const payload = {
                ...userData,
                user_type_id: toNull(userData.user_type_id),
                roll_id: toNull(userData.roll_id),
                deparment_id: toNull(userData.deparment_id),
                // only include profile_picture_url if it has a value
                ...(userData.profile_picture_url ? { profile_picture_url: userData.profile_picture_url } : {}),
                updated_by: current_user_id
            };

            if (isEdit) {
                await axiosInstance.put(`/api/users/${existingUser.id}`, payload);
                Swal.fire({ icon: "success", title: "Updated!", text: "User updated successfully", timer: 1500, showConfirmButton: false });
                navigate(`/user-details/${existingUser.id}`);
            } else {
                // Auto-generate password, force change on first login
                const autoPassword = generatePassword();
                payload.password = autoPassword;
                payload.force_password_change = true;
                payload.created_by = current_user_id;

                const res = await axiosInstance.post("/api/users", payload);
                const new_id = res.data?.data?.id || res.data?.id;
                const new_username = res.data?.data?.user_name || userData.user_name;

                await Swal.fire({
                    title: '✅ User Created!',
                    html: `
                        <div style="text-align:left;background:#f8f9fa;padding:16px;border-radius:8px;margin-top:8px;">
                            <p style="margin-bottom:8px"><strong>Username:</strong> ${new_username}</p>
                            <p style="margin-bottom:8px"><strong>Password:</strong> <span style="font-family:monospace;font-size:15px;background:#e9ecef;padding:2px 6px;border-radius:4px">${autoPassword}</span></p>
                            <p style="margin-bottom:0;color:#6c757d;font-size:12px">⚠️ User will be asked to change this password on first login.</p>
                        </div>
                    `,
                    icon: 'success',
                    confirmButtonText: '📋 Copy & Continue',
                    showCancelButton: true,
                    cancelButtonText: 'Continue',
                    confirmButtonColor: '#3085d6',
                }).then((r) => {
                    if (r.isConfirmed) {
                        navigator.clipboard.writeText(`Username: ${new_username}\nPassword: ${autoPassword}`);
                    }
                });

                navigate(`/user-details/${new_id}`);
            }

        } catch (error) {
            Swal.fire({
                icon: "error",
                title: "Error",
                text: error.response?.data?.message || "Failed to save user"
            });
        }

        setIsSaving(false);
    };

    // ─── Loading ──────────────────────────────────────────────────────────────────

    if (loadingData) {
        return (
            <div className="page-wrapper">
                <div className="content container-fluid d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    // ─── Render ───────────────────────────────────────────────────────────────────

    return (
        <div>
            <div className="page-wrapper">
                <div className="content container-fluid">
                    <div className="card mb-0">
                        <div className="card-body">

                            <div className="page-header">
                                <div className="content-page-header d-flex align-items-center justify-content-between">
                                    <div className="d-flex align-items-center">
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-outline-secondary me-3"
                                            onClick={() => isEdit
                                                ? navigate(`/user-details/${existingUser.id}`)
                                                : navigate('/user')
                                            }
                                        >
                                            <i className="fa fa-arrow-left me-1"></i> Back
                                        </button>
                                        <h5 className="mb-0">{isEdit ? "Edit User" : "Add User"}</h5>
                                    </div>

                                    {isEdit && (
                                        <button
                                            type="button"
                                            className="btn btn-warning btn-sm"
                                            onClick={handleResetPassword}
                                            disabled={isResetting}
                                        >
                                            <i className="fa fa-key me-1"></i>
                                            {isResetting ? "Resetting..." : "Reset Password"}
                                        </button>
                                    )}
                                </div>
                            </div>

                            <form onSubmit={saveUser}>

                                {/* ── Account Info ── */}
                                <h5 className="form-title">Account Information</h5>
                                <div className="row">

                                    <div className="col-md-4 mb-3">
                                        <label>Username *</label>
                                        <input
                                            type="text"
                                            className={`form-control ${errors.user_name ? 'is-invalid' : ''}`}
                                            name="user_name"
                                            value={userData.user_name}
                                            onChange={handleChange}
                                        />
                                        {errors.user_name && <div className="invalid-feedback">{errors.user_name}</div>}
                                    </div>

                                    <div className="col-md-4 mb-3">
                                        <label>Email *</label>
                                        <input
                                            type="email"
                                            className={`form-control ${errors.email_id ? 'is-invalid' : ''}`}
                                            name="email_id"
                                            value={userData.email_id}
                                            onChange={handleChange}
                                        />
                                        {errors.email_id && <div className="invalid-feedback">{errors.email_id}</div>}
                                    </div>

                                    <div className="col-md-4 mb-3">
                                        <label>Phone *</label>
                                        <input
                                            type="text"
                                            className={`form-control ${errors.phone_no ? 'is-invalid' : ''}`}
                                            name="phone_no"
                                            value={userData.phone_no}
                                            onChange={handleChange}
                                        />
                                        {errors.phone_no && <div className="invalid-feedback">{errors.phone_no}</div>}
                                    </div>

                                    <div className="col-md-4 mb-3">
                                        <label>Profile Picture URL</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="profile_picture_url"
                                            value={userData.profile_picture_url}
                                            onChange={handleChange}
                                            placeholder="https://..."
                                        />
                                    </div>

                                    <div className="col-md-8 mb-3 d-flex align-items-end">
                                        <div className={`alert mb-0 w-100 ${isEdit ? 'alert-info' : 'alert-success'}`} style={{ fontSize: '13px' }}>
                                            {isEdit
                                                ? <><i className="fa fa-info-circle me-2"></i>Use the <strong>Reset Password</strong> button (top right) to generate a new password for this user.</>
                                                : <><i className="fa fa-magic me-2"></i>A strong password will be <strong>auto-generated</strong> and shown after saving. User must change it on first login.</>
                                            }
                                        </div>
                                    </div>

                                </div>

                                {/* ── Role & Department ── */}
                                <h5 className="form-title mt-3">Role & Department</h5>
                                <div className="row">

                                    <div className="col-md-4 mb-3">
                                        <label>User Type *</label>
                                        <select
                                            className={`form-control ${errors.user_type_id ? 'is-invalid' : ''}`}
                                            name="user_type_id"
                                            value={userData.user_type_id}
                                            onChange={handleChange}
                                        >
                                            <option value="">Select User Type</option>
                                            {userTypes.map(t => (
                                                <option key={t.id} value={t.id}>{t.name}</option>
                                            ))}
                                        </select>
                                        {errors.user_type_id && <div className="invalid-feedback">{errors.user_type_id}</div>}
                                    </div>

                                    <div className="col-md-4 mb-3">
                                        <label>Role *</label>
                                        <select
                                            className={`form-control ${errors.roll_id ? 'is-invalid' : ''}`}
                                            name="roll_id"
                                            value={userData.roll_id}
                                            onChange={handleChange}
                                        >
                                            <option value="">Select Role</option>
                                            {userRoles.map(r => (
                                                <option key={r.id} value={r.id}>{r.name}</option>
                                            ))}
                                        </select>
                                        {errors.roll_id && <div className="invalid-feedback">{errors.roll_id}</div>}
                                    </div>

                                    <div className="col-md-4 mb-3">
                                        <label>Department *</label>
                                        <select
                                            className={`form-control ${errors.deparment_id ? 'is-invalid' : ''}`}
                                            name="deparment_id"
                                            value={userData.deparment_id}
                                            onChange={handleChange}
                                        >
                                            <option value="">Select Department</option>
                                            {departments.map(d => (
                                                <option key={d.id} value={d.id}>{d.name}</option>
                                            ))}
                                        </select>
                                        {errors.deparment_id && <div className="invalid-feedback">{errors.deparment_id}</div>}
                                    </div>

                                </div>

                                {/* ── Settings ── */}
                                <h5 className="form-title mt-3">Settings</h5>
                                <div className="row">

                                    <div className="col-md-4 mb-3">
                                        <div className="form-check form-switch mt-2">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                name="isactive"
                                                id="isactive"
                                                checked={userData.isactive}
                                                onChange={handleChange}
                                            />
                                            <label className="form-check-label" htmlFor="isactive">Active</label>
                                        </div>
                                    </div>

                                    {isEdit && (
                                        <div className="col-md-6 mb-3">
                                            <div className="form-check form-switch mt-2">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    name="force_password_change"
                                                    id="force_password_change"
                                                    checked={userData.force_password_change}
                                                    onChange={handleChange}
                                                />
                                                <label className="form-check-label" htmlFor="force_password_change">
                                                    Force Password Change on Next Login
                                                </label>
                                            </div>
                                        </div>
                                    )}

                                </div>

                                {/* ── Submit ── */}
                                <div className="text-end mt-4">
                                    <button
                                        type="button"
                                        className="btn btn-secondary me-2"
                                        onClick={() => isEdit
                                            ? navigate(`/user-details/${existingUser.id}`)
                                            : navigate('/user')
                                        }
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-primary"
                                        disabled={isSaving}
                                    >
                                        {isSaving
                                            ? (isEdit ? "Updating..." : "Saving...")
                                            : (isEdit ? "Update User" : "Save User")
                                        }
                                    </button>
                                </div>

                            </form>

                        </div>
                    </div>
                </div>
                <Footer />
            </div>
        </div>
    );
}

export default AddUser;